import React, { forwardRef, useEffect, useImperativeHandle } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getPpiConfigurations, getPpiOrderDetails } from '../Selectors'
import { getActiveAttendantId } from '../../selectors/attendant'
import { getActiveRevenueCenter } from '../../selectors/revenueCenter'
import { getSelectedPromotions } from '../../selectors/promotion'
import { getOrderInProgress } from '../../selectors/order'
import { getCartItems } from '../../selectors/cart'
import { getCurrentMenu, getIsKiosk } from '../../selectors/menus'
import { getTender } from '../Utils'
import { addPaymentToOrderInProgress, updateOrderInProgress } from '../../actions/order'
import { addPromotionsToOrder } from '../../actions/promotion'
import { isEmpty, vn_getEntries } from '../../VNUtil/VNUtils'
import { swapCaseFirstLetter } from '../../VNUtil/Strings'
import { setScannedTenderForOrder } from '../ActionCreators'
import { getPpiStaticCredentialCompareOnly, getPpiSurpressErrorToastMessages } from '../../VNConfigurations/Selectors'
import { getDeviceSerial } from '../../utils/deviceData'

/**
 * A single place to get all state and handle dispatches without having to copy and paste it everywhere
 */
export const VNInjectablePpi = forwardRef((props, ref) => {
  const { onMount } = props
  const dispatch = useDispatch()

  useEffect(() => {
    if (onMount) {
      onMount()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // exposed functions to the parent
  useImperativeHandle(ref, () => ({
    // get all the data used for PPI
    getData(tender) {
      const ppiTender = getTender(ppiConfigurations, tender)
      let customAttributesPosInfo = {}
      const standPpiExtras = activeRevenueCenter.customerAttributes?.ppiExtras

      if (ppiTender?.integration) {
        // swap case becuase of root api camel case swap...ugh
        const integrationUnCapitalized = swapCaseFirstLetter(ppiTender.integration)

        if (standPpiExtras && !isEmpty(standPpiExtras[integrationUnCapitalized])) {
          for (const [key, value] of vn_getEntries(standPpiExtras[integrationUnCapitalized])) {
            customAttributesPosInfo[key] = value
          }
        }
      }

      // tracerId is the "till code" that corresponds to the revenue center UUID
      // if the order has "stand_uuid", it's online and in Stadium
      // if the order does not have it, it's offline and it's a local order, hence we use the active revenue center
      const tracerId = order.standUuid ?? activeRevenueCenter.uuid
      const deviceId = getDeviceSerial()

      return {
        functions: {
          addPromotionsToOrder: (payload) => {
            dispatch(addPromotionsToOrder(payload))
          },
          updateOrderInProgress: (updatedOrder) => {
            dispatch(updateOrderInProgress(updatedOrder))
          },
          addPaymentToOrderInProgress: (payment) => {
            dispatch(addPaymentToOrderInProgress(payment))
          },
          setScannedTenderForOrder: (orderUuid, token, ignoreForReporting = false) => {
            dispatch(setScannedTenderForOrder(orderUuid, token, ignoreForReporting))
          },
        },
        posInfo: {
          userId: activeAttendantId,
          vendorName: activeRevenueCenter.name,
          venueId: process.env.REACT_APP_VENUE_UUID,
          tracerId,
          deviceId,
          ...customAttributesPosInfo,
        },
        promotions: {
          selectedPromotions,
          orderMenuItems,
          menu,
          isKiosk,
        },
        order,
        ppiTender,
        ppiConfigurations,
        ppiOrderDetails,
        ppiStaticCredentialCompareOnly,
        ppiSurpressErrorToastMessages,
      }
    },
  }))

  const ppiConfigurations = useSelector((state) => getPpiConfigurations(state))
  const activeAttendantId = useSelector((state) => getActiveAttendantId(state))
  const activeRevenueCenter = useSelector((state) => getActiveRevenueCenter(state))
  const selectedPromotions = useSelector((state) => getSelectedPromotions(state))
  const order = useSelector((state) => getOrderInProgress(state))
  const orderMenuItems = useSelector(getCartItems)
  const menu = useSelector(getCurrentMenu)
  const isKiosk = useSelector(getIsKiosk)
  const ppiOrderDetails = useSelector((state) => getPpiOrderDetails(state, order.uuid))
  const ppiStaticCredentialCompareOnly = useSelector((state) => getPpiStaticCredentialCompareOnly(state))
  const ppiSurpressErrorToastMessages = useSelector((state) => getPpiSurpressErrorToastMessages(state))

  return <></>
})
